import { useState, useCallback, useEffect } from "react";
import { Modal, Button, Input, Form, InputNumber, Space } from "@pankod/refine-antd";
import toast from "react-hot-toast";

interface ProntoOrderSyncModalProps {
    apiUrl: string;
    isVisible: boolean;
    onClose: () => void;
    orderUid?: string; // Optional orderUid to pre-fill the form
}

interface GiftCard {
    number: string;
    value: number;
}

const { REACT_APP_STRAPI_TOKEN_KEY } = process.env;

/**
 * The CMS rejects this callback for unauthenticated callers (it marks an order
 * as `ordered` and emails the customer a confirmation), so send the CMS editor
 * JWT the same way the other admin services do.
 */
const getAuthHeaders = (): Record<string, string> => {
    const token = localStorage.getItem(`${REACT_APP_STRAPI_TOKEN_KEY}`);
    return token ? { Authorization: `Bearer ${token}` } : {};
};

export const ProntoOrderSyncModal: React.FC<ProntoOrderSyncModalProps> = ({ apiUrl, isVisible, onClose, orderUid: initialOrderUid = "" }) => {
    const [orderUid, setOrderUid] = useState("");
    const [prontoOrderNumber, setProntoOrderNumber] = useState("");
    const [orderHasPossibleIssues, setOrderHasPossibleIssues] = useState(false);
    const [giftCards, setGiftCards] = useState<GiftCard[]>([]);
    const [loading, setLoading] = useState(false);

    const handleAddGiftCard = () => {
        setGiftCards([...giftCards, { number: "", value: 0 }]);
    };

    const handleRemoveGiftCard = (index: number) => {
        const updatedGiftCards = [...giftCards];
        updatedGiftCards.splice(index, 1);
        setGiftCards(updatedGiftCards);
    };

    const handleGiftCardChange = (index: number, field: keyof GiftCard, value: string | number) => {
        const updatedGiftCards = [...giftCards];
        updatedGiftCards[index] = {
            ...updatedGiftCards[index],
            [field]: value
        };
        setGiftCards(updatedGiftCards);
    };

    const handleSyncProntoOrder = useCallback(async () => {
        const currentOrderUid = initialOrderUid || orderUid;
        
        if (!currentOrderUid) {
            toast.error("Order UID is required");
            return;
        }

        if (!prontoOrderNumber) {
            toast.error("Pronto Order Number is required");
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(`${apiUrl}/checkouts/updateprontonumber/${currentOrderUid}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...getAuthHeaders(),
                },
                body: JSON.stringify({
                    prontoOrderNumber,
                    orderHasPossibleIssues,
                    giftCards: giftCards.map(card => ({
                        number: card.number,
                        value: card.value
                    }))
                }),
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || "Failed to sync Pronto order");
            }

            const data = await response.json();
            toast.success(`Pronto order synced successfully: ${data.prontoOrderNumber}`);
            resetForm();
            onClose();
        } catch (error) {
            toast.error(`Error: ${error.message}`);
        } finally {
            setLoading(false);
        }
    }, [apiUrl, orderUid, initialOrderUid, prontoOrderNumber, orderHasPossibleIssues, giftCards, onClose]);

    const resetForm = () => {
        setOrderUid("");
        setProntoOrderNumber("");
        setOrderHasPossibleIssues(false);
        setGiftCards([]);
    };

    // Update local state when initialOrderUid changes or modal becomes visible
    useEffect(() => {
        if (isVisible && initialOrderUid) {
            setOrderUid(initialOrderUid);
        }
    }, [initialOrderUid, isVisible]);
    
    return (
        <Modal
            title="Sync Pronto Order"
            visible={isVisible}
            onCancel={() => {
                resetForm();
                onClose();
            }}
            footer={[
                <Button key="cancel" onClick={() => {
                    resetForm();
                    onClose();
                }}>
                    Cancel
                </Button>,
                <Button key="sync" type="primary" onClick={handleSyncProntoOrder} loading={loading}>
                    Sync Pronto Order
                </Button>,
            ]}
            width={600}
        >
            <Form layout="vertical">
                <Form.Item label="Order UID" required>
                    <Input
                        value={orderUid}
                        onChange={(e) => setOrderUid(e.target.value)}
                        placeholder="Enter order UID"
                        disabled={!!initialOrderUid} // Disable if orderUid is provided
                    />
                </Form.Item>
                
                <Form.Item label="Pronto Order Number" required>
                    <Input
                        value={prontoOrderNumber}
                        onChange={(e) => setProntoOrderNumber(e.target.value)}
                        placeholder="Enter Pronto order number"
                    />
                </Form.Item>
                
                {/* <Form.Item label="Order Has Possible Issues">
                    <Input
                        type="checkbox"
                        checked={orderHasPossibleIssues}
                        onChange={(e) => setOrderHasPossibleIssues(e.target.checked)}
                    />
                </Form.Item> */}
                
                <Form.Item label="Gift Cards">
                    {giftCards.map((card, index) => (
                        <Space key={index} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                            <Input
                                placeholder="Card Number"
                                value={card.number}
                                onChange={(e) => handleGiftCardChange(index, 'number', e.target.value)}
                                style={{ width: '200px' }}
                            />
                            <InputNumber
                                placeholder="Value"
                                value={card.value}
                                onChange={(value) => handleGiftCardChange(index, 'value', value || 0)}
                                style={{ width: '150px' }}
                                min={0}
                                step={0.01}
                                precision={2}
                            />
                            <Button danger onClick={() => handleRemoveGiftCard(index)}>
                                Remove
                            </Button>
                        </Space>
                    ))}
                    <Button type="dashed" onClick={handleAddGiftCard} style={{ width: '100%' }}>
                        + Add Gift Card
                    </Button>
                </Form.Item>
            </Form>
        </Modal>
    );
};
